import { NJSegmentedControl, NJSegmentedControlButton, NJInlineMessage, NJIconButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { UM } from '../_components/tokens';
import { useLayersCatalog, useBuildingColorMode, useProposalVariant } from './hooks';
import { BuildingLayerCard } from './BuildingLayerCard';
import { ProposalATabs } from './ProposalATabs';
import { ProposalBAccordion } from './ProposalBAccordion';

// ─── Meta ─────────────────────────────────────────────────────────────────────

export const meta = {
  title: 'Menu couches',
  description:
    'Refonte du panneau "Couches de données" (icône Couches de la sidebar UrbanMap) : deux propositions comparables, organisées par usage métier plutôt que par catégorie technique.',
  tags: ['map', 'territory', 'network', 'list'],
  author: 'fabien.nesly',
  updatedAt: '2026-10-20T12:47:13+02:00',
  status: 'draft' as const,
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function LayersMenu() {
  const {
    layers,
    filteredLayers,
    favoriteLayers,
    search,
    setSearch,
    toggleVisibility,
    toggleFavorite,
  } = useLayersCatalog();
  const { mode, setMode } = useBuildingColorMode();
  const { variant, setVariant } = useProposalVariant();

  return (
    <PrototypeStudioLayout title="UrbanMap — Menu couches">
      <div style={{ display: 'flex', width: '100%', height: 'calc(100vh - 56px)', overflow: 'hidden' }}>

        {/* ─── Sidebar minimale (contexte visuel) ─── */}
        <nav style={{
          width: 64, minWidth: 64, background: UM.bg, borderRight: `1px solid ${UM.border}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 16, gap: 4,
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 8, background: UM.accentLight,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, color: UM.accent, borderLeft: `3px solid ${UM.accent}`, fontWeight: 600,
          }}>
            <span style={{ fontSize: 18 }}>◇</span>
            <span style={{ fontSize: 10 }}>Couches</span>
          </div>
        </nav>

        {/* ─── Panneau "Couches de données" ─── */}
        <aside style={{
          width: 380, minWidth: 380, background: UM.bg, borderRight: `1px solid ${UM.border}`,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 15,
        }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${UM.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: UM.text }}>Couches de données</span>
              <NJIconButton icon="close" label="Fermer le panneau" scale="sm" variant="tertiary" isDisabled />
            </div>

            <NJSegmentedControl label="Proposition" value={variant} onChange={(v) => setVariant(v as typeof variant)} size="sm">
              <NJSegmentedControlButton label="A — Onglets" value="tabs" />
              <NJSegmentedControlButton label="B — Accordéon" value="accordion" />
            </NJSegmentedControl>
          </div>

          <BuildingLayerCard mode={mode} onModeChange={setMode} />

          {variant === 'tabs' ? (
            <ProposalATabs
              favoriteLayers={favoriteLayers}
              layers={layers}
              toggleVisibility={toggleVisibility}
              toggleFavorite={toggleFavorite}
            />
          ) : (
            <ProposalBAccordion
              favoriteLayers={favoriteLayers}
              layers={layers}
              filteredLayers={filteredLayers}
              search={search}
              setSearch={setSearch}
              toggleVisibility={toggleVisibility}
              toggleFavorite={toggleFavorite}
            />
          )}
        </aside>

        {/* ─── Zone carte (non représentée — hors périmètre de ce prototype) ─── */}
        <main style={{
          flex: 1, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 16, padding: 24,
        }}>
          <div style={{ maxWidth: 420 }}>
            <NJInlineMessage variant="information" title="Prototype de comparaison">
              Ce prototype se concentre sur le panneau "Couches" ouvert depuis l'icône dédiée
              de la sidebar. La carte n'est pas représentée. Utilisez le sélecteur en haut du
              panneau pour comparer la Proposition A (onglets par étape d'étude) et la
              Proposition B (accordéon réorganisé + favoris + recherche).
            </NJInlineMessage>
          </div>
        </main>
      </div>
    </PrototypeStudioLayout>
  );
}
